import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const SkeletonBox = ({ className = '' }) => (
  <div className={`rounded animate-pulse ${className}`} style={{ backgroundColor: '#1f1f1f' }} />
);

const formatShort = (n) => {
  if (!n) return '0';
  if (n >= 1000000) return `${(n / 1000000)?.toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000)?.toFixed(0)}K`;
  return Math.round(n)?.toString();
};

const CustomTooltip = ({ active, payload, label, serverColor }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-lg p-3 border text-xs"
      style={{ backgroundColor: '#1a1a1a', borderColor: '#2a2a2a', fontFamily: 'Inter, sans-serif' }}
    >
      <p className="font-semibold text-white mb-2">{label}</p>
      {payload?.map((entry, i) => (
        <p key={i} style={{ color: entry?.color }}>
          {entry?.name}: {entry?.name === 'Diamonds' ? `💎 ${formatShort(entry?.value)}` : entry?.value}
        </p>
      ))}
    </div>
  );
};

const PerfDailyTrendChart = ({ data, serverColor, loading }) => {
  const { t } = useTranslation();
  const chartData = useMemo(() => {
    const byDate = {};
    data?.forEach((r) => {
      const d = r?.date;
      if (!byDate?.[d]) byDate[d] = { date: d, diamonds: 0, live_streams: 0 };
      byDate[d].diamonds += parseInt(r?.diamonds) || 0;
      byDate[d].live_streams += parseInt(r?.live_streams) || 0;
    });
    return Object.values(byDate)?.sort((a, b) => a?.date?.localeCompare(b?.date));
  }, [data]);

  if (loading) {
    return (
      <div className="rounded-xl p-5 border" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
        <SkeletonBox className="h-64 w-full" />
      </div>
    );
  }

  if (!chartData?.length) {
    return (
      <div
        className="rounded-xl p-5 border flex items-center justify-center h-48"
        style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}
      >
        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>
          {t('admin.performanceActuelle.noDataAvailable')}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl p-5 border" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f1f1f" />
          <XAxis
            dataKey="date"
            tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10, fontFamily: 'Inter, sans-serif' }}
            tickFormatter={(v) => v?.slice(5)}
            axisLine={{ stroke: '#2a2a2a' }}
            tickLine={false}
          />
          <YAxis
            yAxisId="left"
            tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10, fontFamily: 'Inter, sans-serif' }}
            tickFormatter={formatShort}
            axisLine={{ stroke: '#2a2a2a' }}
            tickLine={false}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10, fontFamily: 'Inter, sans-serif' }}
            axisLine={{ stroke: '#2a2a2a' }}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip serverColor={serverColor} />} />
          <Legend
            wrapperStyle={{ fontSize: '11px', fontFamily: 'Inter, sans-serif', color: 'rgba(255,255,255,0.5)' }}
          />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="diamonds"
            name="Diamonds"
            stroke={serverColor}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, fill: serverColor }}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="live_streams"
            name="Live Streams"
            stroke="rgba(255,255,255,0.3)"
            strokeWidth={1.5}
            strokeDasharray="4 2"
            dot={false}
            activeDot={{ r: 3, fill: 'rgba(255,255,255,0.5)' }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PerfDailyTrendChart;
